import React from 'react';
import { BrowserRouter as Router, Routes, Route } from 'react-router-dom';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import { AuthProvider } from './context/AuthContext';
import Navbar from './components/layout/Navbar';
import Home from './components/home/Home';
import Login from './components/auth/Login';
import Register from './components/auth/Register';
import VerifyEmail from './components/auth/VerifyEmail';
import ResendVerification from './components/auth/ResendVerification';
import ForgotPassword from './components/auth/ForgotPassword';
import ResetPassword from './components/auth/ResetPassword';
import Dashboard from './components/dashboard/Dashboard';
import ExamList from './components/exams/ExamList';
import ExamDetail from './components/exams/ExamDetail';
import ExamTaking from './components/exams/ExamTaking';
import ExamResult from './components/exams/ExamResult';
import ArticleList from './components/articles/ArticleList';
import ArticleDetail from './components/articles/ArticleDetail';
import Settings from './components/settings/Settings';
import AdminLayout from './components/admin/AdminLayout';
import AdminDashboard from './components/admin/Dashboard';
import AdminLogin from './components/admin/AdminLogin';
import AdminUsers from './components/admin/Users';
import AdminExams from './components/admin/Exams';
import AdminArticles from './components/admin/Articles';
import AdminSettings from './components/admin/Settings';
import ProtectedRoute from './components/auth/ProtectedRoute';
import AdminProtectedRoute from './components/auth/AdminProtectedRoute';
import MyExams from './components/exams/MyExams';
import About from './components/about/About';
import CreateExam from './components/admin/exams/CreateExam';
import EditExam from './components/admin/exams/EditExam';
import AdminExamsSimple from './components/admin/exams/AdminExamsSimple';
import ExamHistory from './components/exams/ExamHistory';
import AdminManagement from './components/admin/Admins';
import CreateArticle from './components/admin/articles/CreateArticle';
import EditArticle from './components/admin/articles/EditArticle';

// Create a theme instance
const theme = createTheme({
    palette: {
        mode: 'light',
        primary: {
            main: '#1976d2',
        },
        secondary: {
            main: '#dc004e',
        },
    },
    typography: {
        fontFamily: '"Roboto", "Helvetica", "Arial", sans-serif',
        h1: {
            fontSize: '2.5rem',
            fontWeight: 500,
        },
        h2: {
            fontSize: '2rem',
            fontWeight: 500,
        },
        h3: {
            fontSize: '1.75rem',
            fontWeight: 500,
        },
        h4: {
            fontSize: '1.5rem',
            fontWeight: 500,
        },
        h5: {
            fontSize: '1.25rem',
            fontWeight: 500,
        },
        h6: {
            fontSize: '1rem',
            fontWeight: 500,
        },
    },
});

const App = () => {
    return (
        <ThemeProvider theme={theme}>
            <CssBaseline />
            <AuthProvider>
                <Router>
                    <Navbar />
                    <Routes>
                        {/* Public routes */}
                        <Route path="/" element={<Home />} />
                        <Route path="/login" element={<Login />} />
                        <Route path="/register" element={<Register />} />
                        <Route path="/verify-email" element={<VerifyEmail />} />
                        <Route path="/resend-verification" element={<ResendVerification />} />
                        <Route path="/forgot-password" element={<ForgotPassword />} />
                        <Route path="/reset-password" element={<ResetPassword />} />
                        <Route path="/exams" element={<ExamList />} />
                        <Route path="/exams/:id" element={<ExamDetail />} />
                        <Route path="/articles" element={<ArticleList />} />
                        <Route path="/articles/:id" element={<ArticleDetail />} />
                        <Route path="/admin/login" element={<AdminLogin />} />
                        <Route path="/about" element={<About />} />

                        {/* Protected routes */}
                        <Route
                            path="/dashboard"
                            element={
                                <ProtectedRoute>
                                    <Dashboard />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/exams/:id/take"
                            element={
                                <ProtectedRoute>
                                    <ExamTaking />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/exams/:id/result"
                            element={
                                <ProtectedRoute>
                                    <ExamResult />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/settings"
                            element={
                                <ProtectedRoute>
                                    <Settings />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/my-exams"
                            element={
                                <ProtectedRoute>
                                    <MyExams />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/exams/history"
                            element={
                                <ProtectedRoute>
                                    <ExamHistory />
                                </ProtectedRoute>
                            }
                        />
                        <Route
                            path="/exams/results/:id"
                            element={
                                <ProtectedRoute>
                                    <ExamResult />
                                </ProtectedRoute>
                            }
                        />

                        {/* Admin routes */}
                        <Route
                            path="/admin"
                            element={
                                <AdminProtectedRoute>
                                    <AdminLayout />
                                </AdminProtectedRoute>
                            }
                        >
                            <Route index element={<AdminDashboard />} />
                            <Route path="users" element={<AdminUsers />} />
                            <Route path="exams" element={<AdminExamsSimple />} />
                            <Route path="articles" element={<AdminArticles />} />
                            <Route path="articles/create" element={<CreateArticle />} />
                            <Route path="articles/:id/edit" element={<EditArticle />} />
                            <Route path="settings" element={<AdminSettings />} />
                            <Route path="admins" element={<AdminManagement />} />
                            <Route path="exams/create" element={<CreateExam />} />
                            <Route path="exams/:id/edit" element={<EditExam />} />
                        </Route>
                    </Routes>
                </Router>
            </AuthProvider>
        </ThemeProvider>
    );
};

export default App;
